import type { HttpClient } from '../http.js';
import type { CatalogProduct } from '../types.js';

/**
 * Fetches the product catalog for a store.
 *
 * Uses the public `/api/v1/products` endpoint (no auth required).
 */
export class CatalogModule {
  constructor(
    private readonly http: HttpClient,
    private readonly storeId: string,
  ) {}

  /**
   * List all active products for the store, including variants and prices.
   *
   * @param opts.productIds  Optional filter to specific product IDs.
   * @param opts.variantIds  Optional filter to products containing these variant IDs.
   */
  async getProducts(opts?: {
    productIds?: string[];
    variantIds?: string[];
  }): Promise<CatalogProduct[]> {
    const body: Record<string, unknown> = {
      storeId: this.storeId,
      includeVariants: true,
      includePrices: true,
    };
    if (opts?.productIds?.length) body.productIds = opts.productIds;
    if (opts?.variantIds?.length) body.variantIds = opts.variantIds;

    const res = await this.http.post<{ items: CatalogProduct[] }>(
      '/api/v1/products',
      body,
    );

    return res.items ?? [];
  }

  /**
   * Get a single product by ID.
   */
  async getProduct(productId: string): Promise<CatalogProduct | null> {
    const items = await this.getProducts({ productIds: [productId] });
    return items[0] ?? null;
  }
}
